import { UserType } from "@/interfaces";
import { PermissionsMap } from "@/lib/permissions";
import { DEFAULT_CACHE_TIME } from "@/utils/constants";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { SortConfig } from "@/components/DataTable/types";
import api from "./api";
import { serializeFilterParams } from "./queryParams";
import { useUserStore } from "@/stores/userStore";

/**
 * Login request payload
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Login response from backend
 */
export interface LoginResponse {
  message: string;
  userData: string; // JSON stringified user data
  token: string;
}

/**
 * Logout response from backend
 */
export interface LogoutResponse {
  message: string;
}

/**
 * Create user request payload
 */
export interface CreateUserRequest {
  userIdentifier: string;
  firstName: string;
  lastName: string;
  phone?: string;
  designation?: string | null;
  permissions?: PermissionsMap | Record<string, unknown>;
  email: string;
  password: string;
  role?: string;
  isActive?: boolean;
  companyId?: string | null;
}

/**
 * Create user response from backend
 */
export interface CreateUserResponse {
  message: string;
  user: UserType;
}

/**
 * Update user response from backend
 */
export interface UpdateUserResponse {
  message: string;
  user?: UserType;
}

/**
 * Get user by ID request
 */
export interface GetUserByIdRequest {
  _id: string;
}

/**
 * Get user by ID response
 */
export interface GetUserByIdResponse {
  message: string;
  user?: UserType;
}

/**
 * Delete user response from backend
 */
export interface DeleteUserResponse {
  message: string;
}

/**
 * Deactivate user response from backend
 */
export interface DeactivateUserResponse {
  message: string;
}

/**
 * Activate user response from backend
 */
export interface ActivateUserResponse {
  message: string;
}

/**
 * Paginated users response from backend
 */
export interface PaginatedUsersResponse {
  data: UserType[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Current user response from backend
 */
export interface CurrentUserResponse {
  message: string;
  data: UserType;
}

export const useGetCurrentUser = (enabled: boolean = true) => {
  const token = useUserStore((state) => state.token);
  return useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const response = await api.get<{
        message: string;
        data: UserType;
      }>("/current");
      return response.data;
    },
    enabled: enabled && !!token, // Only run query when enabled (i.e., when user is authenticated)
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Authentication service for user login/logout
 */
export const authService = {
  /**
   * Login user with email and password
   * @param credentials - User email and password
   * @returns Login response with user data and token
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>("/login", credentials);
    return response.data;
  },

  /**
   * Logout current user
   * @returns Logout response
   */
  logout: async (): Promise<LogoutResponse> => {
    const response = await api.post<LogoutResponse>("/logout");
    return response.data;
  },
};

export const useUsersListAll = () => {
  return useQuery<PaginatedUsersResponse>({
    queryKey: ["users", "all"],
    queryFn: async () => {
      const response = await api.get("/user");
      // Add fullName field to each user
      const usersWithFullName = response.data.data.map((user: UserType) => ({
        ...user,
        fullName: `${user.firstName} ${user.lastName}`,
      }));
      return {
        ...response.data,
        data: usersWithFullName,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * React Query hook for fetching users of a company who are eligible to be the AdOps POC on an
 * order line item: company members with oms_operations permission = full, active users only.
 */
export const useGetOpsUsersByCompanyId = (companyId: string | null) => {
  return useQuery<PaginatedUsersResponse>({
    queryKey: ["company", companyId, "ops-users"],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID is required");
      const response = await api.get("/user", {
        params: {
          companyId,
          isActive: true,
          permissionKey: "oms_operations",
          permissionValue: "full",
        },
      });
      return response.data;
    },
    enabled: !!companyId,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * React Query hook for fetching users of a company who have the oms_sales permission = full,
 * active users only. Powers the Orders page "Created By" filter.
 */
export const useGetSalesUsersByCompanyId = (companyId: string | null) => {
  return useQuery<PaginatedUsersResponse>({
    queryKey: ["company", companyId, "sales-users"],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID is required");
      const response = await api.get("/user", {
        params: {
          companyId,
          isActive: true,
          permissionKey: "oms_sales",
          permissionValue: "full",
        },
      });
      return response.data;
    },
    enabled: !!companyId,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const useGetUserById = (userId: string | null) => {
  return useQuery<UserType>({
    queryKey: ["user", userId],
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");
      const response = await api.get(`/user/id?_id=${userId}`);
      return response.data.user;
    },
    enabled: !!userId,
    placeholderData: keepPreviousData,
    staleTime: 1000, // 1 second
  });
};

/**
 * User service for user profile management
 */
export const userService = {
  /**
   * Get user by ID
   * @param userId - User ID
   * @returns User data
   */
  getUserById: async (userId: string) => {
    const resp = await api.get(`/user/id?_id=${userId}`);
    return resp.data;
  },

  /**
   * Update user profile
   * @param data - Updated user data
   * @returns Updated user data
   */
  updateUser: async (data: Partial<UserType>): Promise<UpdateUserResponse> => {
    const response = await api.put<UpdateUserResponse>("/user/update", data);
    return response.data;
  },

  /**
   * Create a new user
   * @param data - User creation data
   * @returns Created user data
   */
  createUser: async (data: Partial<UserType>): Promise<CreateUserResponse> => {
    const response = await api.post<CreateUserResponse>("/user/create", data);
    return response.data;
  },

  /**
   * Delete user by ID
   * @param userId - User ID
   * @returns Deletion response
   */
  deleteUser: async (userId: string): Promise<DeleteUserResponse> => {
    const response = await api.delete<DeleteUserResponse>("/user/delete", {
      data: { _id: userId },
    });
    return response.data;
  },

  /**
   * Deactivate user by ID
   * @param userId - User ID
   * @returns Deactivation response
   */
  deactivateUser: async (userId: string): Promise<DeactivateUserResponse> => {
    const response = await api.put<DeactivateUserResponse>("/user/deactivate", {
      _id: userId
    });
    return response.data;
  },


  /**
 * Activate user by ID
 * @param userId - User ID
 * @returns Deactivation response
 */
  activateUser: async (userId: string): Promise<ActivateUserResponse> => {
    const response = await api.put<ActivateUserResponse>("/user/activate", {
      _id: userId
    });
    return response.data;
  },

  /**
   * Upload profile picture
   * @param file - Image file to upload
   * @returns Updated user data with S3 URL
   */
  uploadProfilePicture: async (file: File): Promise<UpdateUserResponse> => {
    const formData = new FormData();
    formData.append("profilePicture", file);

    const response = await api.post<UpdateUserResponse>("/user/profile-picture", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  /**
   * Remove profile picture
   * Deletes the profile picture from S3 and updates user record
   * @returns Updated user data without profile picture
   */
  removeProfilePicture: async (): Promise<UpdateUserResponse> => {
    const response = await api.delete<UpdateUserResponse>("/user/profile-picture");
    console.log("removeProfilePicture service response:", response.data);
    return response.data;
  },

  /**
   * Get all users (paginated)
   * @param params - Query params: page, limit, q (search), filters, signal
   * @returns Paginated user list
   */
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    q?: string;
    sort?: SortConfig;
    filters?: Record<string, unknown>;
    signal?: AbortSignal;
  }): Promise<PaginatedUsersResponse> => {
    const { page = 1, limit = 20, search, q, sort, filters, signal } = params || {};
    const serializedFilters = serializeFilterParams(filters);

    const response = await api.get<PaginatedUsersResponse>("/user", {
      params: {
        page,
        limit,
        search: search || q,
        sortField: sort?.field,
        sortOrder: sort?.order,
        ...serializedFilters,
      },
      signal,
    });

    return response.data;
  },

  /**
   * Reset SSO password for a user
   * Triggers password reset in Cognito which sends temporary password via email
   * @param userId - User ID
   * @returns Success message
   */
  resetSSOPassword: async (userId: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>("/user/reset-sso-password", {
      _id: userId,
    });
    return response.data;
  },

  /**
   * Set permanent password for a user
   * Works for both local and Cognito users
   * @param userId - User ID
   * @param password - Permanent password (min 6 characters)
   * @returns Success message
   */
  setPermanentPassword: async (userId: string, password: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>("/user/set-password", {
      _id: userId,
      password,
    });
    return response.data;
  },

  /**
   * Get user by email
   * Used for user lookup during client/vendor user creation
   * @param email - User email
   * @returns User data or null if not found
   */
  getSimpleUserByEmail: async (email: string): Promise<{ message: string; data: UserType | null }> => {
    const response = await api.get<{ message: string; data: UserType | null }>("/user/simple/email", {
      params: { email },
    });
    return response.data;
  },

  /**
   * Get user by email
   * Used for user lookup during client/vendor user creation
   * @param email - User email
   * @returns User data or null if not found
   */
  getUserByEmail: async (email: string): Promise<{ message: string; data: UserType | null }> => {
    const response = await api.get<{ message: string; data: UserType | null }>("/user/by-email", {
      params: { email },
    });
    return response.data;
  },

  getPlOwnerDetails: async (id: string): Promise<{ message: string; data: UserType| UserType[] | null }> => {
    const response = await api.get<{ message: string; data: UserType | null }>(`/user/get-pl-details/${id}`);
    return response.data;
  },

  getAdminsWithPlMappings: async (companyId: string): Promise<{ message: string; data: Partial<UserType>[] }> => {
    const response = await api.get<{ message: string; data: Partial<UserType>[] }>("/user/admins-with-pl", {
      params: { companyId }
    });
    return response.data;
  },

  assignPlFromAdmin: async (employeeId: string, adminId: string, companyId: string): Promise<{ message: string; data: UserType | null }> => {
    const response = await api.put<{ message: string; data: UserType | null }>(`/user/assign-pl-from-admin/${employeeId}`, {
      adminId,
      companyId
    });
    return response.data;
  },

  removeAdminFromEmployee: async (employeeId: string, adminId: string): Promise<{ message: string; data: null }> => {
    const response = await api.delete<{ message: string; data: null }>(`/user/assign-pl-from-admin/${employeeId}/${adminId}`);
    return response.data;
  },

  assignPlOwners: async (userId: string, plOwnerIds: number[], companyId: string): Promise<{ message: string; data: UserType | null }> => {
    const response = await api.put<{ message: string; data: UserType | null }>(`/user/assign-pl-owner/${userId}`, {
      plOwnerIds,
      companyId
    });
    return response.data;
  }
};
